import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BoardOfflineSettings, PrefetchResult, SyncConflict, FieldConflict, ConflictVoteRecord } from '../types';
import { offlineDb } from './offlineDb';
import { apiCall } from './useKanbanStore';
import { threeWayMerge } from '../features/offline/utils/threeWayMerge';

export interface SyncProgress {
  boardId: string | null;
  current: number;
  total: number;
  phase: 'idle' | 'pushing' | 'done' | 'error';
  errorMessage: string | null;
}

interface OfflineState {
  boardSettings: Record<string, BoardOfflineSettings>;
  isSnapshotting: Record<string, boolean>;
  snapshotError: Record<string, string | null>;
  syncProgress: SyncProgress;
  pendingCounts: Record<string, number>;
  activeConflict: SyncConflict | null;
  openVotes: ConflictVoteRecord[];
  escalatedVoteId: string | null; // vote where deadline passed, no non-initiator votes

  getBoardSettings: (boardId: string) => BoardOfflineSettings | undefined;
  saveBoardSettings: (settings: BoardOfflineSettings) => void;
  snapshotBoard: (boardId: string) => Promise<PrefetchResult>;
  clearBoardSnapshot: (boardId: string) => Promise<void>;
  getOfflineReadyBoards: () => BoardOfflineSettings[];
  syncBoard: (boardId: string) => Promise<void>;
  resolveConflict: (conflict: SyncConflict, resolvedFields: FieldConflict[]) => Promise<void>;
  abortConflict: (conflict: SyncConflict) => Promise<void>;
  refreshPendingCounts: () => Promise<void>;
  dismissSyncProgress: () => void;
  startConflictVote: (conflict: SyncConflict, deadlineHours?: number) => Promise<void>;
  pollConflictVotes: (boardId: string, currentUserId: string) => Promise<void>;
  submitVote: (voteId: string, choices: Record<string, 'local' | 'server'>) => Promise<void>;
  applyVoteResolution: (vote: ConflictVoteRecord) => Promise<void>;
  abortVoteConflict: (voteId: string) => Promise<void>;
  dismissEscalation: () => void;
}

export const REFRESH_INTERVAL_OPTIONS = [5, 10, 15, 30, 60] as const;

const IDLE_PROGRESS: SyncProgress = {
  boardId: null,
  current: 0,
  total: 0,
  phase: 'idle',
  errorMessage: null,
};

export const useOfflineStore = create<OfflineState>()(
  persist(
    (set, get) => ({
      boardSettings: {},
      isSnapshotting: {},
      snapshotError: {},
      syncProgress: IDLE_PROGRESS,
      pendingCounts: {},
      activeConflict: null,
      openVotes: [],
      escalatedVoteId: null,

      getBoardSettings: (boardId) => get().boardSettings[boardId],

      saveBoardSettings: (settings) => {
        set((state) => ({
          boardSettings: { ...state.boardSettings, [settings.boardId]: settings },
        }));
      },

      snapshotBoard: async (boardId) => {
        set((state) => ({
          isSnapshotting: { ...state.isSnapshotting, [boardId]: true },
          snapshotError: { ...state.snapshotError, [boardId]: null },
        }));

        try {
          const res = await apiCall(`/api/offline/prefetch?boardId=${encodeURIComponent(boardId)}`);
          const data: PrefetchResult = await res.json();

          await offlineDb.transaction(
            'rw',
            [offlineDb.projects, offlineDb.columns, offlineDb.tasks],
            async () => {
              const existingCols = await offlineDb.columns
                .where('projectId')
                .equals(boardId)
                .toArray();
              const existingColIds = existingCols.map((c) => c.id);
              if (existingColIds.length > 0) {
                await offlineDb.tasks.where('columnId').anyOf(existingColIds).delete();
              }
              await offlineDb.columns.where('projectId').equals(boardId).delete();
              await offlineDb.projects.delete(boardId);

              if (data.project) await offlineDb.projects.put(data.project);
              if (data.columns?.length) await offlineDb.columns.bulkPut(data.columns);
              if (data.tasks?.length) await offlineDb.tasks.bulkPut(data.tasks);
            },
          );

          const snapshotAt = new Date().toISOString();
          set((state) => ({
            boardSettings: {
              ...state.boardSettings,
              [boardId]: { ...state.boardSettings[boardId], lastSnapshotAt: snapshotAt },
            },
          }));

          return data;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Snapshot failed';
          set((state) => ({
            snapshotError: { ...state.snapshotError, [boardId]: message },
          }));
          throw err;
        } finally {
          set((state) => ({
            isSnapshotting: { ...state.isSnapshotting, [boardId]: false },
          }));
        }
      },

      clearBoardSnapshot: async (boardId) => {
        const cols = await offlineDb.columns.where('projectId').equals(boardId).toArray();
        const colIds = cols.map((c) => c.id);
        if (colIds.length > 0) {
          await offlineDb.tasks.where('columnId').anyOf(colIds).delete();
        }
        await offlineDb.columns.where('projectId').equals(boardId).delete();
        await offlineDb.projects.delete(boardId);

        set((state) => ({
          boardSettings: {
            ...state.boardSettings,
            [boardId]: { ...state.boardSettings[boardId], lastSnapshotAt: null },
          },
        }));
      },

      getOfflineReadyBoards: () =>
        Object.values(get().boardSettings).filter((s) => s.enabled && s.lastSnapshotAt !== null),

      syncBoard: async (boardId) => {
        set({ syncProgress: { boardId, current: 0, total: 0, phase: 'pushing', errorMessage: null } });

        try {
          const all = await offlineDb.syncQueue.where('boardId').equals(boardId).toArray();
          const items = all
            .filter((i) => i.status === 'pending' || i.status === 'failed')
            .sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));

          set((state) => ({ syncProgress: { ...state.syncProgress, total: items.length } }));

          if (items.length === 0) {
            set({ syncProgress: { boardId, current: 0, total: 0, phase: 'done', errorMessage: null } });
            return;
          }

          // Upload pending offline files before pushing operations that reference them
          const pendingFiles = await offlineDb.fileCache
            .filter((f) => f.fileUrl.startsWith('offline://'))
            .toArray();

          const urlRemap: Record<string, string> = {};
          for (const cached of pendingFiles) {
            try {
              const blob = new Blob([cached.blob], { type: cached.mimeType });
              const formData = new FormData();
              formData.append('file', blob, cached.fileName);
              const uploadRes = await apiCall('/api/upload', { method: 'POST', body: formData });
              const uploaded = await uploadRes.json() as { fileUrl: string };
              urlRemap[cached.fileUrl] = uploaded.fileUrl;
              await offlineDb.fileCache.delete(cached.fileUrl);
            } catch (fileErr) {
              console.warn('Failed to upload cached file:', fileErr);
            }
          }

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            set((state) => ({ syncProgress: { ...state.syncProgress, current: i + 1 } }));

            if (item.id !== undefined) {
              await offlineDb.syncQueue.update(item.id, { status: 'syncing' });
            }

            // Replace any offline:// URLs in the operation data with real URLs
            let data = item.data;
            if (Object.keys(urlRemap).length > 0) {
              const dataStr = JSON.stringify(data);
              let replaced = dataStr;
              for (const [tempUrl, realUrl] of Object.entries(urlRemap)) {
                replaced = replaced.split(tempUrl).join(realUrl);
              }
              if (replaced !== dataStr) data = JSON.parse(replaced) as Record<string, unknown>;
            }

            try {
              const res = await apiCall('/api/sync/push', {
                method: 'POST',
                body: JSON.stringify({
                  operation: item.operation,
                  entityType: item.entityType,
                  entityId: item.entityId,
                  boardId: item.boardId,
                  data,
                  baseVersion: item.baseVersion,
                }),
              });
              const result = await res.json() as {
                status: string;
                serverData?: Record<string, unknown>;
              };

              if (result.status === 'conflict' && result.serverData && item.baseVersion) {
                // Three-way merge to detect true field conflicts
                const baseData = item.data;
                const localData = item.data;
                const serverData = result.serverData;
                const { conflicts } = threeWayMerge(
                  baseData as Record<string, unknown>,
                  localData as Record<string, unknown>,
                  serverData,
                );

                if (conflicts.length > 0) {
                  if (item.id !== undefined) {
                    await offlineDb.syncQueue.update(item.id, { status: 'conflict' });
                  }
                  const title =
                    (item.data.title as string) ||
                    (item.data.name as string) ||
                    `${item.entityType} ${item.entityId.slice(0, 8)}`;
                  set({
                    activeConflict: {
                      queueItemId: item.id!,
                      boardId: item.boardId,
                      entityType: item.entityType,
                      entityId: item.entityId,
                      entityTitle: title,
                      fields: conflicts,
                      createdAt: new Date().toISOString(),
                    },
                    syncProgress: { boardId, current: i + 1, total: items.length, phase: 'idle', errorMessage: null },
                  });
                  return; // Pause sync — user must resolve
                }
              }

              if (item.id !== undefined) {
                await offlineDb.syncQueue.update(item.id, { status: 'done' });
              }
            } catch (err) {
              if (item.id !== undefined) {
                await offlineDb.syncQueue.update(item.id, {
                  status: 'failed',
                  retries: (item.retries || 0) + 1,
                });
              }
              console.warn('Failed to push sync item:', err);
            }
          }

          // Remove successfully synced items
          await offlineDb.syncQueue
            .where('boardId')
            .equals(boardId)
            .and((i) => i.status === 'done')
            .delete();

          // Refresh pending counts
          await get().refreshPendingCounts();

          set((state) => ({
            syncProgress: { ...state.syncProgress, phase: 'done' },
          }));
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Sync failed';
          set((state) => ({
            syncProgress: { ...state.syncProgress, phase: 'error', errorMessage },
          }));
        }
      },

      resolveConflict: async (conflict, resolvedFields) => {
        // Build the resolved data object from user's field-by-field choices
        const resolvedData: Record<string, unknown> = {};
        for (const f of resolvedFields) {
          if (f.resolution === 'local') {
            resolvedData[f.field] = f.localValue;
          }
          // 'server' resolution means we keep server value — don't push that field
        }

        if (Object.keys(resolvedData).length > 0) {
          // Push the resolved fields to server
          try {
            await apiCall('/api/sync/push', {
              method: 'POST',
              body: JSON.stringify({
                operation: 'update',
                entityType: conflict.entityType,
                entityId: conflict.entityId,
                boardId: conflict.boardId,
                data: resolvedData,
                baseVersion: null, // resolution is authoritative
              }),
            });
          } catch (err) {
            console.warn('Failed to push resolved conflict:', err);
          }
        }

        // Mark queue item as done
        await offlineDb.syncQueue.update(conflict.queueItemId, { status: 'done' });
        set({ activeConflict: null });
        await get().refreshPendingCounts();

        // Resume sync for remaining items
        void get().syncBoard(conflict.boardId);
      },

      abortConflict: async (conflict) => {
        // Mark the conflicted item as failed/aborted
        await offlineDb.syncQueue.update(conflict.queueItemId, { status: 'failed' });
        set({ activeConflict: null });
        await get().refreshPendingCounts();
      },

      refreshPendingCounts: async () => {
        const all = await offlineDb.syncQueue
          .filter((i) => i.status === 'pending' || i.status === 'failed')
          .toArray();
        const counts: Record<string, number> = {};
        for (const item of all) {
          counts[item.boardId] = (counts[item.boardId] ?? 0) + 1;
        }
        set({ pendingCounts: counts });
      },

      dismissSyncProgress: () => {
        set({ syncProgress: IDLE_PROGRESS });
      },

      startConflictVote: async (conflict, deadlineHours = 24) => {
        const res = await apiCall('/api/conflicts', {
          method: 'POST',
          body: JSON.stringify({
            boardId: conflict.boardId,
            entityType: conflict.entityType,
            entityId: conflict.entityId,
            entityTitle: conflict.entityTitle,
            fields: conflict.fields,
            deadlineHours,
          }),
        });
        if (!res.ok) {
          const err = await res.json() as { error?: string };
          throw new Error(err.error ?? 'Failed to start vote');
        }
        // Mark the queue item as paused (conflict) — will be resolved when vote settles
        await offlineDb.syncQueue.update(conflict.queueItemId, { status: 'conflict' });
        set({ activeConflict: null });
      },

      pollConflictVotes: async (boardId, currentUserId) => {
        try {
          const res = await apiCall(`/api/conflicts?boardId=${encodeURIComponent(boardId)}`);
          if (!res.ok) return;
          const votes = await res.json() as ConflictVoteRecord[];
          const now = new Date();

          // Check for escalated votes: deadline passed AND no non-initiator votes AND current user is initiator
          let escalatedId: string | null = null;
          for (const v of votes) {
            const deadlinePassed = new Date(v.deadline) < now;
            const nonInitiatorVotes = v.votes.filter((vt) => vt.userId !== v.initiatorUserId);
            if (deadlinePassed && nonInitiatorVotes.length === 0 && v.initiatorUserId === currentUserId) {
              escalatedId = v.id;
              break;
            }
          }

          set({ openVotes: votes, escalatedVoteId: escalatedId });
        } catch {
          // Polling errors are non-fatal
        }
      },

      submitVote: async (voteId, choices) => {
        const res = await apiCall(`/api/conflicts/${encodeURIComponent(voteId)}/vote`, {
          method: 'POST',
          body: JSON.stringify({ choices }),
        });
        if (!res.ok) {
          const err = await res.json() as { error?: string };
          throw new Error(err.error ?? 'Failed to submit vote');
        }
        const result = await res.json() as { votes: ConflictVoteRecord['votes']; resolution: Record<string, 'local' | 'server'> };
        set((state) => ({
          openVotes: state.openVotes.map((v) =>
            v.id === voteId ? { ...v, votes: result.votes } : v,
          ),
        }));
      },

      applyVoteResolution: async (vote) => {
        // Build resolved data: fields where resolution=local get pushed; server fields are skipped
        const resolvedData: Record<string, unknown> = {};
        if (vote.resolution) {
          for (const f of vote.fields) {
            if (vote.resolution[f.field] === 'local') {
              resolvedData[f.field] = f.localValue;
            }
          }
        }

        if (Object.keys(resolvedData).length > 0) {
          await apiCall('/api/sync/push', {
            method: 'POST',
            body: JSON.stringify({
              operation: 'update',
              entityType: vote.entityType,
              entityId: vote.entityId,
              boardId: vote.boardId,
              data: resolvedData,
              baseVersion: null,
            }),
          });
        }

        await apiCall(`/api/conflicts/${encodeURIComponent(vote.id)}/resolve`, {
          method: 'POST',
          body: JSON.stringify({ resolution: vote.resolution }),
        });

        // Clear the queue item for this entity
        const queueItems = await offlineDb.syncQueue
          .where('entityId').equals(vote.entityId)
          .filter((i) => i.status === 'conflict')
          .toArray();
        for (const item of queueItems) {
          if (item.id !== undefined) await offlineDb.syncQueue.update(item.id, { status: 'done' });
        }
        await offlineDb.syncQueue.filter((i) => i.status === 'done').delete();

        set((state) => ({
          openVotes: state.openVotes.filter((v) => v.id !== vote.id),
          escalatedVoteId: state.escalatedVoteId === vote.id ? null : state.escalatedVoteId,
        }));
        await get().refreshPendingCounts();
      },

      abortVoteConflict: async (voteId) => {
        await apiCall(`/api/conflicts/${encodeURIComponent(voteId)}/abort`, { method: 'POST' });

        // Mark related queue items as failed
        const vote = get().openVotes.find((v) => v.id === voteId);
        if (vote) {
          const queueItems = await offlineDb.syncQueue
            .where('entityId').equals(vote.entityId)
            .filter((i) => i.status === 'conflict')
            .toArray();
          for (const item of queueItems) {
            if (item.id !== undefined) await offlineDb.syncQueue.update(item.id, { status: 'failed' });
          }
        }

        set((state) => ({
          openVotes: state.openVotes.filter((v) => v.id !== voteId),
          escalatedVoteId: state.escalatedVoteId === voteId ? null : state.escalatedVoteId,
        }));
        await get().refreshPendingCounts();
      },

      dismissEscalation: () => {
        set({ escalatedVoteId: null });
      },
    }),
    {
      name: 'kan-do-offline',
      partialize: (state) => ({ boardSettings: state.boardSettings }),
    },
  ),
);
